import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BadgeDollarSign,
  CircleAlert,
  Clock3,
  CreditCard,
  FilePlus2,
  Loader2,
  ShieldAlert,
  SquareArrowOutUpRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  formatDateTime,
  formatMoney,
  type DepotApproval,
  type DepotAuditEntry,
  type DepotAuditResponse,
  type DepotBillableCharge,
  type DepotBillingRun,
  type DepotBillingRunDetail,
  useDepotList,
} from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type BillingAction = 'handoff' | 'post';

interface DepotBatchBillingResult {
  run: {
    id: string;
    runRef: string;
    runMode: string;
    status: string;
    totalAmount: number;
    itemCount: number;
    groupCount: number;
    firstEntryId: string | null;
    rerunOfRunId: string | null;
  } | null;
  entries: Array<{
    entryId: string;
    entryNumber: string;
    entryStatus: string;
    billToPartyId: string;
    billToName?: string | null;
    currency: string;
    chargeCount: number;
    totalAmount: number;
    created: boolean;
  }>;
  linkedCharges: number;
}

export default function DepotBillingPage() {
  const navigate = useNavigate();
  const { items, loading, error, refresh } = useDepotList<DepotBillableCharge>('/depot/charges/billable?limit=100');
  const handoffs = useDepotList<DepotBillableCharge>('/depot/charges/handoffs?limit=12');
  const runs = useDepotList<DepotBillingRun>('/depot/billing-runs?limit=12');

  const [editing, setEditing] = useState<DepotBillableCharge | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [historyCharge, setHistoryCharge] = useState<DepotBillableCharge | null>(null);
  const [historyItems, setHistoryItems] = useState<DepotAuditEntry[]>([]);
  const [historyApprovals, setHistoryApprovals] = useState<DepotApproval[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [runDetailOpen, setRunDetailOpen] = useState(false);
  const [runDetailLoading, setRunDetailLoading] = useState(false);
  const [runDetail, setRunDetail] = useState<DepotBillingRunDetail | null>(null);

  const pageError: DataErrorDetail | null = error
    ? { endpoint: '/depot/charges/billable', message: error, status: 500, code: 'DEPOT_BILLING_LOAD_FAILED' }
    : null;

  const summary = useMemo(() => {
    const totalAmount = items.reduce((sum, item) => sum + Number(item.amount ?? 0), 0);
    const ready = items.filter((item) => item.charge_status === 'unbilled' && item.bill_to_party_id);
    const readyCount = ready.length;
    const blockedCount = items.length - readyCount;
    const readyGroups = new Set(ready.map((item) => `${item.bill_to_party_id}:${item.currency}`)).size;
    return { totalAmount, readyCount, blockedCount, readyGroups };
  }, [items]);

  const openHistory = async (item: DepotBillableCharge) => {
    setHistoryCharge(item);
    setHistoryLoading(true);
    try {
      const result = await api.get<DepotAuditResponse>(`/depot/charges/${item.id}/audit-trail`);
      setHistoryItems(result.items ?? []);
      setHistoryApprovals(result.approvals ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load charge history');
      setHistoryItems([]);
      setHistoryApprovals([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openRunDetail = async (runId: string) => {
    setRunDetailOpen(true);
    setRunDetailLoading(true);
    try {
      const detail = await api.get<DepotBillingRunDetail>(`/depot/billing-runs/${runId}`);
      setRunDetail(detail);
    } catch (err) {
      setRunDetail(null);
      toast.error(err instanceof Error ? err.message : 'Failed to load depot billing run detail');
    } finally {
      setRunDetailLoading(false);
    }
  };

  const handleDispute = async () => {
    if (!editing || !reason.trim()) {
      toast.error('Dispute reason is required');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/depot/charges/${editing.id}/dispute`, { reason: reason.trim() });
      toast.success(`Charge ${editing.charge_no} disputed`);
      setEditing(null);
      setReason('');
      await Promise.all([refresh(), handoffs.refresh(), runs.refresh()]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to dispute charge');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestApproval = async () => {
    if (!editing || !reason.trim()) {
      toast.error('Dispute reason is required');
      return;
    }
    setRequestingApproval(true);
    try {
      const approval = await api.post<{ id: string; status: string }>(`/depot/charges/${editing.id}/request-dispute-approval`, {
        reason: reason.trim(),
      });
      toast.success(`Approval ${approval.id} created`);
      setEditing(null);
      setReason('');
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to request dispute approval');
    } finally {
      setRequestingApproval(false);
    }
  };

  const handleAction = async (item: DepotBillableCharge, action: BillingAction) => {
    const key = `${item.id}:${action}`;
    setBusyKey(key);
    try {
      const result = await api.post<{
        entryId: string;
        entryNumber: string;
        entryStatus: string;
      }>(action === 'post' ? `/depot/charges/${item.id}/post-ledger` : `/depot/charges/${item.id}/handoff-ledger`, {});
      toast.success(
        action === 'post'
          ? `${item.charge_no} posted as ${result.entryNumber}`
          : `${item.charge_no} handed off as draft ${result.entryNumber}`,
      );
      await Promise.all([refresh(), handoffs.refresh(), runs.refresh()]);
      navigate(`/finance/billing?entryId=${encodeURIComponent(result.entryId)}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to process depot billing action');
    } finally {
      setBusyKey(null);
    }
  };

  const handleBatchAction = async (action: BillingAction) => {
    const key = `batch:${action}`;
    setBusyKey(key);
    try {
      const result = await api.post<DepotBatchBillingResult>(
        action === 'post' ? '/depot/charges/post-ledger-batch' : '/depot/charges/handoff-ledger-batch',
        {},
      );
      if (result.entries.length === 0) {
        toast.success('No ready depot charges to batch');
        return;
      }
      const firstEntry = result.entries[0];
      if (!firstEntry) {
        toast.error('Batch result did not return a ledger entry');
        return;
      }
      toast.success(
        action === 'post'
          ? `${result.linkedCharges} charge(s) posted into ${result.entries.length} ledger document(s)`
          : `${result.linkedCharges} charge(s) handed off into ${result.entries.length} draft document(s)`,
      );
      await Promise.all([refresh(), handoffs.refresh(), runs.refresh()]);
      navigate(`/finance/billing?entryId=${encodeURIComponent(firstEntry.entryId)}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to process depot batch billing action');
    } finally {
      setBusyKey(null);
    }
  };

  const handleRerun = async (run: DepotBillingRun) => {
    const key = `rerun:${run.id}`;
    setBusyKey(key);
    try {
      const result = await api.post<DepotBatchBillingResult>(`/depot/billing-runs/${run.id}/rerun`, {});
      if (result.entries.length === 0) {
        toast.success(`No eligible depot charges left for rerun ${run.run_ref}`);
        return;
      }
      const firstEntry = result.entries[0];
      if (!firstEntry) {
        toast.error('Rerun result did not return a ledger entry');
        return;
      }
      toast.success(
        `${run.run_ref} rerun completed: ${result.linkedCharges} charge(s), ${result.entries.length} document(s)`,
      );
      await Promise.all([refresh(), handoffs.refresh(), runs.refresh()]);
      if (runDetail?.run.id === run.id) {
        await openRunDetail(run.id);
      }
      navigate(`/finance/billing?entryId=${encodeURIComponent(firstEntry.entryId)}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rerun depot billing batch');
    } finally {
      setBusyKey(null);
    }
  };

  const handleRunItemStatus = async (
    itemId: string,
    status: 'resolved' | 'skipped' | 'pending',
    runId: string,
  ) => {
    const key = `run-item:${itemId}:status:${status}`;
    setBusyKey(key);
    try {
      await api.post(`/depot/billing-run-items/${itemId}/mark-status`, { status });
      toast.success(`Run item marked as ${status}`);
      await Promise.all([runs.refresh(), openRunDetail(runId)]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update run item status');
    } finally {
      setBusyKey(null);
    }
  };

  const handleRunItemRerun = async (itemId: string, runId: string) => {
    const key = `run-item:${itemId}:rerun`;
    setBusyKey(key);
    try {
      const result = await api.post<{
        itemId: string;
        itemStatus: string;
        ledgerEntryId: string | null;
        ledgerEntryNumber: string | null;
      }>(`/depot/billing-run-items/${itemId}/rerun`, {});
      toast.success(result.ledgerEntryNumber ? `Run item rerun into ${result.ledgerEntryNumber}` : 'Run item rerun completed');
      await Promise.all([refresh(), handoffs.refresh(), runs.refresh(), openRunDetail(runId)]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rerun run item');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Billing" onRetry={refresh} />}

      <div>
        <h2 className="text-lg font-semibold">Billing Desk</h2>
        <p className="text-sm text-muted-foreground">
          Review depot charge events, freeze disputes, then hand off ready items into Finance as draft AR or posted ledger entries.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={summary.readyCount === 0 || busyKey !== null} onClick={() => handleBatchAction('handoff')}>
            {busyKey === 'batch:handoff' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FilePlus2 className="mr-1.5 h-4 w-4" />}
            Batch Draft {summary.readyGroups > 0 ? `(${summary.readyGroups} groups)` : ''}
          </Button>
          <Button size="sm" disabled={summary.readyCount === 0 || busyKey !== null} onClick={() => handleBatchAction('post')}>
            {busyKey === 'batch:post' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BadgeDollarSign className="mr-1.5 h-4 w-4" />}
            Batch Post {summary.readyGroups > 0 ? `(${summary.readyGroups} groups)` : ''}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="text-xs text-muted-foreground">Billing Queue</div>
          <div className="text-2xl font-semibold">{loading ? '...' : items.length}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="text-xs text-muted-foreground">Ready to Hand Off</div>
          <div className="text-2xl font-semibold">{loading ? '...' : summary.readyCount}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="text-xs text-muted-foreground">Exposure in Queue</div>
          <div className="text-2xl font-semibold">{loading ? '...' : formatMoney(summary.totalAmount, 'SGD')}</div>
        </div>
      </div>

      {!loading && summary.blockedCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-3 text-sm text-amber-900">
          <CircleAlert className="h-4 w-4" />
          {summary.blockedCount} charge(s) are blocked because bill-to is unresolved or the item is under dispute.
        </div>
      )}

      <DepotGridPanel title="Billing Queue" subtitle="Activity-based charges by row. Batch actions stay at page level; single-charge actions stay inline.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Charge</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Activity</th>
              <th className={depotThClassName}>Amount</th>
              <th className={depotThClassName}>Bill To</th>
              <th className={depotThClassName}>Source</th>
              <th className={depotThClassName}>Status</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <DepotEmptyRow colSpan={8}>Loading billing queue...</DepotEmptyRow>
            ) : items.length === 0 ? (
              <DepotEmptyRow colSpan={8}><CreditCard className="mr-1 inline h-4 w-4" />No depot charges pending billing action.</DepotEmptyRow>
            ) : (
              items.map((item) => {
                const canBill = item.charge_status === 'unbilled' && Boolean(item.bill_to_party_id);
                const handoffKey = `${item.id}:handoff`;
                const postKey = `${item.id}:post`;
                return (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className={`${depotTdClassName} font-mono font-semibold`}>{item.charge_no}</td>
                    <td className={depotTdClassName}>
                      <div className="font-medium">{item.container_no}</div>
                      <div className="text-xs text-muted-foreground">{item.depot_code ?? '—'}</div>
                    </td>
                    <td className={depotTdClassName}>{item.charge_type}</td>
                    <td className={depotTdClassName}>{formatMoney(item.amount, item.currency)}</td>
                    <td className={depotTdClassName}>
                      <div>{item.bill_to_name ?? 'Unresolved bill-to'}</div>
                      <div className="text-xs text-muted-foreground">{item.line_operator ?? 'Line operator TBC'}</div>
                    </td>
                    <td className={`${depotTdClassName} text-xs text-muted-foreground`}>
                      <div>Visit {item.visit_ref ?? '—'}</div>
                      <div>Ref {item.event_ref ?? '—'} · {formatDateTime(item.event_at)}</div>
                    </td>
                    <td className={depotTdClassName}>
                      <Badge variant={canBill ? 'outline' : item.charge_status === 'disputed' ? 'destructive' : 'secondary'}>
                        {canBill ? item.charge_status : item.charge_status === 'unbilled' ? 'bill-to missing' : item.charge_status}
                      </Badge>
                      {item.dispute_reason ? <div className="mt-1 text-xs text-amber-700">{item.dispute_reason}</div> : null}
                    </td>
                    <td className={depotTdClassName}>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => openHistory(item)}>
                          <Clock3 className="mr-1.5 h-4 w-4" />
                          History
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditing(item); setReason(item.dispute_reason ?? ''); }}>
                          <ShieldAlert className="mr-1.5 h-4 w-4" />
                          Dispute
                        </Button>
                        <Button size="sm" variant="outline" disabled={!canBill || busyKey !== null} onClick={() => handleAction(item, 'handoff')}>
                          {busyKey === handoffKey ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FilePlus2 className="mr-1.5 h-4 w-4" />}
                          Draft
                        </Button>
                        <Button size="sm" disabled={!canBill || busyKey !== null} onClick={() => handleAction(item, 'post')}>
                          {busyKey === postKey ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BadgeDollarSign className="mr-1.5 h-4 w-4" />}
                          Post
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Recent Handoffs" subtitle="Depot charges already linked to Finance. Open ledger documents directly from the row.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Charge</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Bill To</th>
              <th className={depotThClassName}>Amount</th>
              <th className={depotThClassName}>Ledger</th>
              <th className={depotThClassName}>Linked / Posted</th>
              <th className={depotThClassName}>Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {handoffs.loading ? (
              <DepotEmptyRow colSpan={7}>Loading recent handoffs...</DepotEmptyRow>
            ) : handoffs.items.length === 0 ? (
              <DepotEmptyRow colSpan={7}>No recent handoffs yet.</DepotEmptyRow>
            ) : (
              handoffs.items.map((item) => (
                <tr key={`${item.id}:${item.billing_entry_id ?? 'no-entry'}`} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.charge_no}</td>
                  <td className={depotTdClassName}>{item.container_no} · {item.depot_code ?? '—'}</td>
                  <td className={depotTdClassName}>{item.bill_to_name ?? item.line_operator ?? 'Bill-to TBC'}</td>
                  <td className={depotTdClassName}>{formatMoney(item.amount, item.currency)}</td>
                  <td className={depotTdClassName}>
                    <div>{item.billing_entry_number ?? 'Ledger entry TBC'}</div>
                    <Badge variant="outline">{item.billing_entry_status ?? 'draft'}</Badge>
                  </td>
                  <td className={`${depotTdClassName} text-xs text-muted-foreground`}>
                    <div>Linked {formatDateTime(item.updated_at ?? item.event_at)}</div>
                    <div>Posted {formatDateTime(item.billing_posted_at)}</div>
                  </td>
                  <td className={depotTdClassName}>
                    {item.billing_entry_id ? (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/finance/billing?entryId=${encodeURIComponent(item.billing_entry_id!)}`)}>
                        <SquareArrowOutUpRight className="mr-1.5 h-4 w-4" />
                        Open Finance
                      </Button>
                    ) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel title="Billing Run History" subtitle="Batch history, recovery detail, and rerun actions.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Run</th>
              <th className={depotThClassName}>Mode</th>
              <th className={depotThClassName}>Items / Groups</th>
              <th className={depotThClassName}>Amount</th>
              <th className={depotThClassName}>Created</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {runs.loading ? (
              <DepotEmptyRow colSpan={6}>Loading billing runs...</DepotEmptyRow>
            ) : runs.items.length === 0 ? (
              <DepotEmptyRow colSpan={6}>No billing runs yet.</DepotEmptyRow>
            ) : (
              runs.items.map((run) => {
                const rerunKey = `rerun:${run.id}`;
                return (
                  <tr key={run.id} className="hover:bg-muted/30">
                    <td className={`${depotTdClassName} font-mono font-semibold`}>{run.run_ref}</td>
                    <td className={depotTdClassName}><Badge variant="outline">{run.run_mode}</Badge></td>
                    <td className={depotTdClassName}>{run.item_count} items · {run.group_count} groups</td>
                    <td className={depotTdClassName}>{formatMoney(run.total_amount, 'SGD')}</td>
                    <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(run.created_at)}</td>
                    <td className={depotTdClassName}>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => openRunDetail(run.id)}>
                          <Clock3 className="mr-1.5 h-4 w-4" />
                          Detail
                        </Button>
                        <Button size="sm" variant="outline" disabled={!run.first_entry_id || busyKey !== null} onClick={() => navigate(`/finance/billing?entryId=${encodeURIComponent(run.first_entry_id!)}`)}>
                          <SquareArrowOutUpRight className="mr-1.5 h-4 w-4" />
                          First Entry
                        </Button>
                        <Button size="sm" disabled={busyKey !== null} onClick={() => handleRerun(run)}>
                          {busyKey === rerunKey ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BadgeDollarSign className="mr-1.5 h-4 w-4" />}
                          Rerun
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) { setEditing(null); setReason(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dispute Charge</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Reason for dispute" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setReason(''); }}>Cancel</Button>
            <Button variant="outline" onClick={handleRequestApproval} disabled={requestingApproval || saving}>
              {requestingApproval ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Clock3 className="mr-1.5 h-4 w-4" />}
              Request Approval
            </Button>
            <Button onClick={handleDispute} disabled={saving}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldAlert className="mr-1.5 h-4 w-4" />}
              Submit Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!historyCharge} onOpenChange={(open) => { if (!open) { setHistoryCharge(null); setHistoryItems([]); setHistoryApprovals([]); } }}>
        <SheetContent className="w-full sm:max-w-[1000px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{historyCharge ? `Charge History · ${historyCharge.charge_no}` : 'Charge History'}</SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            <section className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Approvals</div>
              {historyLoading ? (
                <div className="text-sm text-muted-foreground">Loading approval history…</div>
              ) : historyApprovals.length === 0 ? (
                <div className="text-sm text-muted-foreground">No approval records yet.</div>
              ) : (
                <table className={depotTableClassName}>
                  <thead className={depotTableHeadClassName}>
                    <tr>
                      <th className={depotThClassName}>Approval</th>
                      <th className={depotThClassName}>Type</th>
                      <th className={depotThClassName}>Reviewer</th>
                      <th className={depotThClassName}>Status</th>
                      <th className={depotThClassName}>Created / Decided</th>
                      <th className={depotThClassName}>Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historyApprovals.map((item) => (
                      <tr key={item.id}>
                        <td className={depotTdClassName}>{item.title}</td>
                        <td className={depotTdClassName}>{item.request_type}</td>
                        <td className={depotTdClassName}>{item.reviewer_role ?? '—'}</td>
                        <td className={depotTdClassName}>
                          <Badge variant={item.status === 'approved' ? 'secondary' : item.status === 'pending' ? 'outline' : 'destructive'}>
                            {item.status}
                          </Badge>
                        </td>
                        <td className={`${depotTdClassName} text-xs text-muted-foreground`}>
                          <div>{formatDateTime(item.created_at)}</div>
                          <div>{formatDateTime(item.decided_at)}</div>
                        </td>
                        <td className={`${depotTdClassName} text-xs text-rose-600`}>{item.decision_reason ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Activity</div>
              {historyLoading ? (
                <div className="text-sm text-muted-foreground">Loading activity…</div>
              ) : historyItems.length === 0 ? (
                <div className="text-sm text-muted-foreground">No activity yet.</div>
              ) : (
                <table className={depotTableClassName}>
                  <thead className={depotTableHeadClassName}>
                    <tr>
                      <th className={depotThClassName}>Action</th>
                      <th className={depotThClassName}>Entity</th>
                      <th className={depotThClassName}>Actor</th>
                      <th className={depotThClassName}>Changed Fields</th>
                      <th className={depotThClassName}>Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {historyItems.map((item) => (
                      <tr key={item.id}>
                        <td className={depotTdClassName}>{item.action}</td>
                        <td className={depotTdClassName}>{item.entity_type}</td>
                        <td className={depotTdClassName}>{item.actor_id ?? item.actor_type}</td>
                        <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{item.changes?.fields?.join(', ') ?? '—'}</td>
                        <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={runDetailOpen} onOpenChange={(open) => { if (!open) { setRunDetailOpen(false); setRunDetail(null); } }}>
        <SheetContent className="w-full sm:max-w-[1000px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{runDetail ? `Billing Run · ${runDetail.run.run_ref}` : 'Billing Run Detail'}</SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {runDetailLoading ? (
              <div className="text-sm text-muted-foreground">Loading run detail…</div>
            ) : !runDetail ? (
              <div className="text-sm text-muted-foreground">No billing run detail loaded.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 rounded-lg border bg-background p-3 text-sm md:grid-cols-4">
                  <div>
                    <div className="text-xs text-muted-foreground">Mode</div>
                    <div className="font-medium">{runDetail.run.run_mode}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Status</div>
                    <div className="font-medium">{runDetail.run.status}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Items</div>
                    <div className="font-medium">{runDetail.run.item_count}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Amount</div>
                    <div className="font-medium">{formatMoney(runDetail.run.total_amount, 'SGD')}</div>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-lg border">
                  <table className={depotTableClassName}>
                    <thead className={depotTableHeadClassName}>
                      <tr>
                        <th className={depotThClassName}>Charge</th>
                        <th className={depotThClassName}>Bill To</th>
                        <th className={depotThClassName}>Amount</th>
                        <th className={depotThClassName}>Ledger</th>
                        <th className={depotThClassName}>Status</th>
                        <th className={depotThClassName}>Error</th>
                        <th className={depotThClassName}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {runDetail.items.map((item) => {
                        const statusKeyBase = `run-item:${item.id}:status`;
                        const rerunKey = `run-item:${item.id}:rerun`;
                        return (
                          <tr key={item.id}>
                            <td className={`${depotTdClassName} font-mono font-medium`}>{item.charge_no ?? 'Charge TBC'}</td>
                            <td className={depotTdClassName}>{item.bill_to_name ?? 'Bill-to TBC'}</td>
                            <td className={depotTdClassName}>{formatMoney(item.amount, item.currency)}</td>
                            <td className={depotTdClassName}>
                              <div>{item.ledger_entry_number ?? '—'}</div>
                              <div className="text-xs text-muted-foreground">{item.ledger_entry_status ?? '—'}</div>
                            </td>
                            <td className={depotTdClassName}>
                              <Badge variant={item.item_status === 'posted' ? 'secondary' : item.item_status === 'skipped' ? 'destructive' : 'outline'}>
                                {item.item_status}
                              </Badge>
                            </td>
                            <td className={`${depotTdClassName} text-xs text-amber-800`}>{item.error_message ?? '—'}</td>
                            <td className={depotTdClassName}>
                              <div className="flex flex-wrap gap-2">
                                <Button size="sm" variant="outline" disabled={busyKey !== null} onClick={() => handleRunItemStatus(item.id, 'resolved', runDetail.run.id)}>
                                  {busyKey === `${statusKeyBase}:resolved` ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                                  Resolve
                                </Button>
                                <Button size="sm" variant="outline" disabled={busyKey !== null} onClick={() => handleRunItemStatus(item.id, 'skipped', runDetail.run.id)}>
                                  {busyKey === `${statusKeyBase}:skipped` ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                                  Skip
                                </Button>
                                <Button size="sm" variant="outline" disabled={busyKey !== null} onClick={() => handleRunItemStatus(item.id, 'pending', runDetail.run.id)}>
                                  {busyKey === `${statusKeyBase}:pending` ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                                  Reopen
                                </Button>
                                {!item.ledger_entry_id ? (
                                  <Button size="sm" disabled={busyKey !== null} onClick={() => handleRunItemRerun(item.id, runDetail.run.id)}>
                                    {busyKey === rerunKey ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BadgeDollarSign className="mr-1.5 h-4 w-4" />}
                                    Rerun
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="outline" onClick={() => navigate(`/finance/billing?entryId=${encodeURIComponent(item.ledger_entry_id!)}`)}>
                                    <SquareArrowOutUpRight className="mr-1.5 h-4 w-4" />
                                    Open Entry
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
